import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api/api';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { X, PaperPlaneRight, Chats, Paperclip, FolderOpen, File as FileIcon, Receipt } from '@phosphor-icons/react';
import QuoteFormDialog from './QuoteFormDialog';
import { toast } from 'sonner';

const statusLabel = (s) => ({
  pending: 'In attesa', pending_pro: 'In attesa', notified: 'Inviata', confirmed: 'Confermata',
  accepted: 'Accettata', in_progress: 'In corso', completed: 'Completata', reviewed: 'Completata',
  cancelled: 'Annullata', payment_required: 'Pagamento richiesto',
}[s] || s);

const roleLabel = (r) => ({
  client: 'Cliente', professional: 'Professionista', center: 'Centro', admin: 'Admin', system: 'Avviso',
}[r] || r);

const BookingChat = ({ bookingId, requestId, onClose, forceReadonly = false }) => {
  const { user } = useAuth();
  const base = requestId ? `/api/requests/${requestId}/messages` : `/api/bookings/${bookingId}/messages`;
  const [messages, setMessages] = useState([]);
  const [readonly, setReadonly] = useState(false);
  const [readonlyReason, setReadonlyReason] = useState('');
  const [internalOnly, setInternalOnly] = useState(false);
  const [centerDirect, setCenterDirect] = useState(false);
  const [isCenter, setIsCenter] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [contactLocked, setContactLocked] = useState(false);
  const [bookingStatus, setBookingStatus] = useState('');
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [recordPickerOpen, setRecordPickerOpen] = useState(false);
  const [recordFiles, setRecordFiles] = useState(null);
  const [uploading, setUploading] = useState(false);
  const listRef = useRef(null);
  const fileInputRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const r = await api.get(`${base}?limit=200`);
      setMessages(r.data.messages || []);
      setReadonly(!!r.data.readonly);
      setReadonlyReason(r.data.readonly_reason || '');
      setInternalOnly(!!r.data.internal_only);
      setCenterDirect(!!r.data.center_direct);
      setIsCenter(r.data.my_role === 'center');
      setContactLocked(!!r.data.contact_locked);
      setBookingStatus(r.data.booking_status || r.data.request_status || '');
      // auto-scroll bottom
      setTimeout(() => {
        if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
      }, 50);
    } catch (e) {
      toast.error('Errore chat');
    }
  }, [base]);

  useEffect(() => {
    fetchMessages();
    const t = setInterval(fetchMessages, 15000);
    return () => clearInterval(t);
  }, [fetchMessages]);

  const send = async (e) => {
    e?.preventDefault();
    if ((!text.trim() && pendingAttachments.length === 0) || readonly || forceReadonly) return;
    setSending(true);
    try {
      await api.post(base, {
        body: text.trim(),
        attachments: pendingAttachments,
      });
      setText('');
      setPendingAttachments([]);
      fetchMessages();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore invio');
    } finally { setSending(false); }
  };

  const pickFile = () => fileInputRef.current?.click();
  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Max 10MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('doc_type', 'chat');
      const r = await api.post('/api/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPendingAttachments((prev) => [...prev, {
        file_id: r.data.file_id,
        label: file.name,
        content_type: file.type,
      }]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore upload');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };
  const removePending = (idx) => setPendingAttachments((prev) => prev.filter((_, i) => i !== idx));

  const openRecordPicker = async () => {
    setRecordPickerOpen(true);
    if (recordFiles !== null) return;
    try {
      const r = await api.get(`/api/requests/${requestId}/record-attachments`);
      setRecordFiles(r.data.items || []);
    } catch (err) {
      setRecordFiles([]);
      toast.error(err.response?.data?.detail || 'Cartella non disponibile');
    }
  };

  const addRecordAttachment = (f) => {
    setPendingAttachments((prev) => (
      prev.some((p) => p.file_id === f.file_id)
        ? prev
        : [...prev, { file_id: f.file_id, label: f.label, content_type: f.content_type }]
    ));
    setRecordPickerOpen(false);
  };

  const openAttachment = async (a) => {
    try {
      const r = await api.get(`/api/files/${a.file_id}`, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: a.content_type || r.data.type || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Impossibile aprire il file');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="chat-modal">
      <div className="bg-background rounded-2xl w-full max-w-lg h-[70vh] flex flex-col shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-primary/5">
          <div className="flex items-center gap-2">
            <Chats size={20} weight="duotone" className="text-primary" />
            <div>
              <div className="font-medium text-sm">{requestId ? 'Chat prestazione' : 'Chat prenotazione'}</div>
              <div className="text-xs text-foreground/60">
                {requestId ? `Richiesta ${requestId.slice(0, 12)}` : `Booking ${bookingId.slice(0, 12)}`} · {statusLabel(bookingStatus)}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-full" data-testid="chat-close-btn">
            <X size={18} />
          </button>
        </div>

        <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-muted/20" data-testid="chat-messages">
          {contactLocked && (
            <p className="text-center text-[11px] text-foreground/60 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid="chat-contact-locked">
              I dati completi del cliente (cognome, telefono, indirizzo) si sbloccano quando la data è confermata.
            </p>
          )}
          {centerDirect && (
            <p className="text-center text-[11px] text-sky-900 bg-sky-50 border border-sky-200 rounded-xl px-3 py-2" data-testid="chat-center-direct">
              Stai scrivendo direttamente al cliente. Quando assegni la richiesta a un tuo professionista, la chat passa a lui e tu potrai scrivere solo note interne.
            </p>
          )}
          {internalOnly && (
            <p className="text-center text-[11px] text-sky-900 bg-sky-50 border border-sky-200 rounded-xl px-3 py-2" data-testid="chat-internal-only">
              Come Centro leggi tutta la conversazione; quello che scrivi è una nota interna visibile solo al tuo professionista, mai al cliente.
            </p>
          )}
          {messages.length === 0 && (
            <p className="text-center text-sm text-foreground/60 py-6">Nessun messaggio. Scrivi il primo!</p>
          )}
          {messages.map((m) => {
            const mine = m.sender_id === user?.user_id;
            return (
              <div key={m.message_id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`} data-testid={`msg-${m.message_id}`}>
                <div className={`max-w-[75%] rounded-2xl px-3 py-2 ${mine ? 'bg-primary text-primary-foreground' : 'bg-background border border-border'} ${m.audience === 'internal' ? 'ring-1 ring-sky-300' : ''}`}>
                  {m.audience === 'internal' && (
                    <div className="text-[9px] uppercase tracking-wide mb-1 opacity-80">Nota interna</div>
                  )}
                  {!mine && (
                    <div className="flex items-center gap-1 mb-1">
                      <span className="text-xs font-medium">{m.sender_name}</span>
                      <Badge variant="outline" className="text-[9px]">{roleLabel(m.sender_role)}</Badge>
                    </div>
                  )}
                  {m.body && <p className="text-sm whitespace-pre-wrap">{m.body}</p>}
                  {(m.attachments || []).length > 0 && (
                    <div className="mt-2 space-y-1">
                      {m.attachments.map((a) => (
                        <button
                          key={a.file_id}
                          type="button"
                          onClick={() => openAttachment(a)}
                          className={`w-full flex items-center gap-2 p-2 rounded-lg text-xs text-left ${mine ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-muted'}`}
                          data-testid={`msg-att-${a.file_id}`}
                        >
                          <FileIcon size={14} weight="duotone" />
                          <span className="truncate flex-1">{a.label || a.file_id}</span>
                          {a.from_record && <span className="text-[9px] uppercase opacity-70">cartella</span>}
                          <span className="underline">apri</span>
                        </button>
                      ))}
                    </div>
                  )}
                  <div className={`text-[10px] mt-1 ${mine ? 'text-primary-foreground/70' : 'text-foreground/50'}`}>
                    {new Date(m.created_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {(readonly || forceReadonly) ? (
          <div className="p-4 border-t border-border text-center text-sm text-foreground/60 bg-muted/40" data-testid="chat-readonly-note">
            {forceReadonly ? 'Visualizzazione amministratore (sola lettura)'
              : readonlyReason || `Chat chiusa (${statusLabel(bookingStatus)})`}
          </div>
        ) : (
          <form onSubmit={send} className="p-3 border-t border-border">
            {pendingAttachments.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-2" data-testid="chat-pending-attachments">
                {pendingAttachments.map((a, i) => (
                  <div key={i} className="flex items-center gap-1 px-2 py-1 rounded-full bg-muted text-xs">
                    <FileIcon size={12} />
                    <span className="truncate max-w-[140px]">{a.label}</span>
                    <button type="button" onClick={() => removePending(i)} className="hover:text-red-600" data-testid={`remove-pending-${i}`}>
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {recordPickerOpen && (
              <div className="mb-2 rounded-xl border border-[#E3F2FD] bg-muted/30 p-2 max-h-40 overflow-y-auto" data-testid="chat-record-picker">
                <div className="flex items-center justify-between px-1 pb-1">
                  <span className="text-xs font-medium text-foreground/80">Dalla cartella del paziente</span>
                  <button type="button" onClick={() => setRecordPickerOpen(false)} className="text-foreground/50 hover:text-foreground" data-testid="chat-record-picker-close">
                    <X size={14} />
                  </button>
                </div>
                {recordFiles === null ? (
                  <p className="text-xs text-foreground/60 px-1 py-2">Caricamento referti…</p>
                ) : recordFiles.length === 0 ? (
                  <p className="text-xs text-foreground/60 px-1 py-2" data-testid="chat-record-empty">
                    Nessun referto o foto nella cartella assistenziale.
                  </p>
                ) : recordFiles.map((f) => (
                  <button
                    key={f.file_id}
                    type="button"
                    onClick={() => addRecordAttachment(f)}
                    className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-background flex items-center gap-2 text-xs"
                    data-testid={`chat-record-file-${f.file_id}`}
                  >
                    <FileIcon size={14} className="text-primary shrink-0" />
                    <span className="truncate flex-1">{f.label}</span>
                    {f.created_at && (
                      <span className="text-[10px] text-foreground/50">
                        {new Date(f.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
            <div className="flex items-end gap-2">
              <input
                ref={fileInputRef}
                type="file"
                onChange={onFilePicked}
                className="hidden"
                accept="image/*,application/pdf,.doc,.docx"
                data-testid="chat-file-input"
              />
              <Button
                type="button"
                variant="outline"
                onClick={pickFile}
                disabled={uploading || sending}
                className="rounded-full h-10 w-10 p-0"
                data-testid="chat-attach-btn"
                title="Allega documento"
              >
                <Paperclip size={16} />
              </Button>
              {requestId && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={openRecordPicker}
                  disabled={uploading || sending}
                  className="rounded-full h-10 w-10 p-0"
                  data-testid="chat-record-btn"
                  title="Allega dalla cartella del paziente"
                >
                  <FolderOpen size={16} />
                </Button>
              )}
              {requestId && isCenter && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setQuoteOpen(true)}
                  className="rounded-full h-10 w-10 p-0"
                  data-testid="chat-quote-btn"
                  title="Invia un preventivo al cliente"
                >
                  <Receipt size={16} />
                </Button>
              )}
              <Textarea
                rows={1}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder={uploading ? 'Caricamento…' : (internalOnly ? 'Nota interna per il tuo professionista…' : 'Scrivi un messaggio…')}
                className="flex-1 min-h-0"
                data-testid="chat-input"
              />
              <Button
                type="submit"
                disabled={sending || uploading || (!text.trim() && pendingAttachments.length === 0)}
                className="rounded-full"
                data-testid="chat-send-btn"
              >
                <PaperPlaneRight size={16} weight="fill" />
              </Button>
            </div>
          </form>
        )}
      </div>
      {requestId && isCenter && (
        <QuoteFormDialog open={quoteOpen} onOpenChange={setQuoteOpen} presetRequestId={requestId} onCreated={fetchMessages} />
      )}
    </div>
  );
};

export default BookingChat;
